import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import type { AppDispatch } from "_shared/_stores/store-types";
import downloadFile from "_shared/_utilities/download-file";
import { aiAssistantApi } from "ai-assistant/_api/ai-assistant-api-service";
import AssistantAttachment from "ai-assistant/attachments/assistant-attachment";
import { ATTACHMENT_GONE_ISSUE } from "ai-assistant/attachments/assistant-attachment-failure";
import React from "react";

const Files = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: theme.spacing(1, 0, 0),
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const fileLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: "0.85rem",
  textAlign: "left",
  wordBreak: "break-all",
} as const;

const TypeLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  marginLeft: theme.spacing(0.5),
}));

/**
 * Fetches one attached file with the session's token and saves it.
 *
 * Dispatched without a subscription and refetched on every click, as a cited document is, so the
 * bytes are not kept and each download is checked afresh. At module level because a `try` holding
 * conditionals makes the React compiler skip the whole component.
 */
async function downloadAttachment(dispatch: AppDispatch, attachment: AssistantAttachment): Promise<void> {
  try {
    const response = await dispatch(aiAssistantApi.endpoints.downloadAssistantAttachment.initiate(
      { attachmentId: attachment.id.value },
      { subscribe: false, forceRefetch: true },
    )).unwrap();
    await downloadFile(response, attachment.fileName);
  } catch (error) {
    showErrorSnackbar(error, {
      doing: "downloading your file",
      operation: "assistant.attachment.download",
      missingMessage: ATTACHMENT_GONE_ISSUE,
    });
  }
}

function SentAttachment({ attachment }: Readonly<{ attachment: AssistantAttachment }>) {
  const dispatch = useAttorneyHubDispatch();
  const [downloading, setDownloading] = React.useState(false);

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    await downloadAttachment(dispatch, attachment);
    setDownloading(false);
  }

  return (
    <li>
      <Link component="button" type="button" onClick={handleDownload} disabled={downloading}
        aria-busy={downloading} aria-label={`Download ${attachment.fileName}`} sx={fileLinkSx}>
        <InsertDriveFileIcon fontSize="small" />
        {attachment.fileName}
        <TypeLabel>{attachment.typeLabel}</TypeLabel>
      </Link>
    </li>
  );
}

/** The files a sent message carried, under it, each one a download (#6504). */
export default function SentAttachmentList({ attachments }: Readonly<{ attachments: readonly AssistantAttachment[] }>) {
  if (attachments.length === 0) return null;

  return (
    <Files aria-label="Files sent with this message">
      {attachments.map((attachment) => (
        <SentAttachment key={attachment.id.value} attachment={attachment} />
      ))}
    </Files>
  );
}
